import { useState } from 'react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import type { EstadoPlan } from '@actual-app/core/server/rumbo/plan-app';
import * as monthUtils from '@actual-app/core/shared/months';
import type { IntegerAmount } from '@actual-app/core/shared/util';
import { useQueryClient } from '@tanstack/react-query';

import { useBudgetActions } from '#budget/mutations';
import type { ApplyBudgetActionPayload } from '#budget/mutations';
import { categoryQueries } from '#budget/queries';
import { useEnvelopeSheetValue } from '#components/budget/envelope/EnvelopeBudgetComponents';
import {
  importeDePlantilla,
  notaConObjetivo,
} from '#components/mobile/budget/objetivos';
import { parsePagosFijos } from '#components/mobile/budget/pagadas';
import {
  ediciones,
  edicionPendiente,
  versionVista,
} from '#components/mobile/novedades/catalogo';
import { Bocadillo } from '#components/mobile/travesia/Bocadillo';
import * as escribir from '#components/mobile/travesia/escribir';
import {
  actualizarTravesia,
  leerTravesia,
  travesiaPendiente,
} from '#components/mobile/travesia/estado';
import type { DatosRumbo } from '#components/mobile/travesia/pasoRumbo';
import { useEuros } from '#components/mobile/travesia/pasos/comun';
import { Boton } from '#components/mobile/ui/Boton';
import { Hoja } from '#components/mobile/ui/Hoja';
import { color, texto } from '#components/mobile/ui/tokens';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useCategories } from '#hooks/useCategories';
import { useLocalPref } from '#hooks/useLocalPref';
import { useMetadataPref } from '#hooks/useMetadataPref';
import { useNavigate } from '#hooks/useNavigate';
import { usePlan } from '#hooks/usePlan';
import { SheetNameProvider } from '#hooks/useSheetName';
import { pushModal } from '#modals/modalsSlice';
import { saveSyncedPrefs } from '#prefs/prefsSlice';
import { useDispatch, useSelector, useStore } from '#redux';
import { envelopeBudget } from '#spreadsheet/bindings';

import {
  avisoInicio,
  CLAVE_PAGO,
  claveHito,
  escribirPrefPrueba,
  finPrueba,
  leerPrefPrueba,
  lineasTrasPago,
  situacionRumbo,
  sobraConDescuento,
  textoHito,
  textoIngreso,
  textoPago,
} from './avisosPrueba';
import type { AvisoInicio, SituacionRumbo } from './avisosPrueba';

/**
 * Gavi y la categoría Rumbo durante la prueba (Vadik, 1-nov-2026): en Inicio,
 * una hoja el día 30, el 43 y el último día de la prueba, cuando ya hay con
 * qué apartar lo que quedó pendiente, y tras el primer pago («¿Apunto el
 * cobro?»). Cada una sale una vez; nada se mueve sin que la persona toque.
 * Espera a las otras ventanas de una vez (permiso de uso, novedades).
 */
export function AvisoPruebaRumbo({
  esperar = false,
  novedadesVistas = false,
}: {
  esperar?: boolean;
  /** Las novedades de la versión, si las había, ya se cerraron. */
  novedadesVistas?: boolean;
}) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  // Otra ventana de una vez va primero (permiso, novedades de la versión).
  const [hayNovedades] = useState(
    () => edicionPendiente(ediciones(t), versionVista()) != null,
  );
  const { plan } = usePlan();
  const { data: { list: categorias } = { list: [] } } = useCategories();
  const travesiaRaw = useSelector(state => state.prefs.synced.travesia);
  const prefRaw = useSelector(state => state.prefs.synced['rumbo-prueba']);
  const [soloLectura] = useMetadataPref('rumboSoloLectura');
  const [cerrada, setCerrada] = useState(false);
  if (
    !plan ||
    cerrada ||
    soloLectura ||
    esperar ||
    (hayNovedades && !novedadesVistas) ||
    !pathname.startsWith('/inicio')
  ) {
    return null;
  }
  const travesia = leerTravesia(travesiaRaw);
  const pref = leerPrefPrueba(prefRaw);
  const idGuardado = pref.id ?? travesia?.ids?.rumbo?.id;
  const id = categorias.some(c => c.id === idGuardado && !c.hidden)
    ? (idGuardado ?? null)
    : null;
  const hoy = monthUtils.currentDay();
  const fin = plan.gratisHasta != null ? finPrueba(plan.gratisHasta) : hoy;
  const mesFin = monthUtils.monthFromDate(fin);
  return (
    <SheetNameProvider
      name={monthUtils.sheetForMonth(monthUtils.currentMonth())}
    >
      <LeerListo>
        {listo => (
          <ConSaldo id={id} mes={mesFin}>
            {({ saldo, asignado }) => {
              const precio = plan.precios?.mensual ?? 0;
              const situacion = situacionRumbo(id != null, saldo, precio);
              const aviso = avisoInicio({
                plan,
                hoy,
                pref,
                situacion,
                pendiente:
                  pref.pendiente === true ||
                  travesia?.ids?.rumbo?.pendiente === true,
                listo,
                tutorialEnMarcha: travesiaPendiente(travesia),
              });
              if (!aviso) {
                return null;
              }
              return (
                <HojaAviso
                  aviso={aviso}
                  plan={plan}
                  id={id}
                  fin={fin}
                  situacion={situacion}
                  listo={listo ?? 0}
                  asignado={asignado}
                  planificado={
                    travesia?.ids?.rumbo?.id === id &&
                    !travesia?.ids?.rumbo?.pendiente &&
                    !pref.visto.includes(claveHito(fin, 'ingreso'))
                  }
                  onCerrar={() => setCerrada(true)}
                />
              );
            }}
          </ConSaldo>
        )}
      </LeerListo>
    </SheetNameProvider>
  );
}

/** «Listo para asignar» del mes de la hoja (null mientras carga). */
function LeerListo({
  children,
}: {
  children: (listo: IntegerAmount | null) => ReactNode;
}) {
  const listo = useEnvelopeSheetValue(envelopeBudget.toBudget);
  return children(typeof listo === 'number' ? listo : null);
}

type SaldoRumbo = { saldo: IntegerAmount | null; asignado: IntegerAmount };

/**
 * Saldo y asignado de Rumbo en `mes`; sin categoría, null. Mientras carga,
 * nada.
 */
function ConSaldo({
  id,
  mes,
  children,
}: {
  id: string | null;
  mes: string;
  children: (s: SaldoRumbo) => ReactNode;
}) {
  if (!id) {
    return children({ saldo: null, asignado: 0 });
  }
  return (
    <SheetNameProvider name={monthUtils.sheetForMonth(mes)}>
      <LeerSaldo id={id}>{children}</LeerSaldo>
    </SheetNameProvider>
  );
}

function LeerSaldo({
  id,
  children,
}: {
  id: string;
  children: (s: SaldoRumbo) => ReactNode;
}) {
  const saldo = useEnvelopeSheetValue(envelopeBudget.catBalance(id));
  const asignado = useEnvelopeSheetValue(envelopeBudget.catBudgeted(id));
  if (typeof saldo !== 'number') {
    return null;
  }
  return children({
    saldo,
    asignado: typeof asignado === 'number' ? asignado : 0,
  });
}

function HojaAviso({
  aviso,
  plan,
  id,
  fin,
  situacion,
  listo,
  asignado,
  planificado,
  onCerrar,
}: {
  aviso: AvisoInicio;
  plan: EstadoPlan;
  id: string | null;
  fin: string;
  situacion: SituacionRumbo;
  listo: IntegerAmount;
  /** Lo asignado a Rumbo en el mes del fin de la prueba. */
  asignado: IntegerAmount;
  planificado: boolean;
  onCerrar: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { eur } = useEuros();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const store = useStore();
  const queryClient = useQueryClient();
  const applyBudgetAction = useBudgetActions();
  const [, setMesPlan] = useLocalPref('budget.startMonth');
  const [ocupado, setOcupado] = useState(false);

  const precio = plan.precios?.mensual ?? 0;
  const mesFin = monthUtils.monthFromDate(fin);
  const fecha = new Intl.DateTimeFormat(i18n.language, {
    day: 'numeric',
    month: 'long',
  }).format(new Date(`${fin}T12:00:00`));
  const datos: DatosRumbo = {
    fin,
    dia: monthUtils.getDay(fin),
    mes: mesFin,
    precio,
  };

  function prefs() {
    return store.getState().prefs.synced;
  }

  async function cerrar(
    vistas: string[],
    cambios: { id?: string; pendiente?: boolean } = {},
    otras: Record<string, string> = {},
  ) {
    onCerrar();
    await dispatch(
      saveSyncedPrefs({
        prefs: {
          ...otras,
          'rumbo-prueba': escribirPrefPrueba(
            leerPrefPrueba(prefs()['rumbo-prueba']),
            { ...cambios, vistas },
          ),
        },
      }),
    );
  }

  const vistas = aviso.tipo === 'pago' ? [CLAVE_PAGO] : aviso.vistas;

  const abrirPlan = () => {
    setMesPlan(mesFin);
    void cerrar(vistas);
    void navigate('/budget');
  };

  const elegirDeDonde = () => {
    if (!id) {
      return;
    }
    void cerrar(vistas);
    dispatch(
      pushModal({
        modal: {
          name: 'mover',
          options: {
            month: mesFin,
            origen: id,
            modoInicial: 'hacia',
            onBudgetAction: (month: string, type: string, args: unknown) =>
              applyBudgetAction.mutate({
                month,
                type,
                args,
              } as ApplyBudgetActionPayload),
          },
        },
      }),
    );
  };

  const seguirConRumbo = () => {
    void cerrar(vistas);
    void navigate('/settings', { state: { abrir: 'plan' } });
  };

  /** Crea la categoría (o la recupera) y aparta el precio si hay con qué. */
  const crear = async () => {
    setOcupado(true);
    try {
      const res = await escribir.crearRumbo(send, t, 'Rumbo', datos, {
        mes: monthUtils.currentMonth(),
        hoy: monthUtils.currentDay(),
        coloresRaw: prefs()['category-colors'],
        ids: undefined,
      });
      const hayDinero = listo >= precio;
      if (hayDinero) {
        await escribir.apartarRumbo(send, res.id, datos);
      }
      const fijos = new Set(parsePagosFijos(prefs()['pagos-fijos']));
      fijos.add(res.id);
      await cerrar(
        vistas,
        { id: res.id, pendiente: !hayDinero },
        {
          'category-colors': res.coloresRaw,
          'pagos-fijos': JSON.stringify([...fijos]),
        },
      );
      await queryClient.invalidateQueries({
        queryKey: categoryQueries.lists(),
      });
    } finally {
      setOcupado(false);
    }
  };

  /** Ya hay con qué: suma lo que falta a lo asignado en el mes del fin. */
  const apartar = async (falta: IntegerAmount) => {
    if (!id) {
      return;
    }
    setOcupado(true);
    try {
      await send('budget/budget-amount', {
        month: mesFin,
        category: id,
        amount: asignado + falta,
      });
      const travesia = leerTravesia(prefs().travesia);
      await cerrar(
        vistas,
        { pendiente: false },
        travesia?.ids?.rumbo?.id === id
          ? {
              travesia: actualizarTravesia(prefs().travesia, {
                ids: { rumbo: { id } },
              }),
            }
          : {},
      );
    } finally {
      setOcupado(false);
    }
  };

  /** «¿Apunto el cobro?»: objetivo con lo que se paga y el movimiento. */
  const apuntarCobro = async () => {
    if (aviso.tipo !== 'pago' || !id) {
      return;
    }
    setOcupado(true);
    try {
      const proximo =
        plan.pagadoHasta != null
          ? monthUtils.dayFromDate(new Date(plan.pagadoHasta * 1000))
          : monthUtils.addMonths(monthUtils.currentDay(), 1);
      const previa = await send('notes-get', { id });
      await send('notes-save', {
        id,
        note: notaConObjetivo(
          previa?.note,
          lineasTrasPago(aviso.cobro, proximo, importeDePlantilla),
        ),
      });
      await send('budget/store-note-templates', [id]);
      await send('budget/refresh-goals', { month: monthUtils.currentMonth() });
      await cerrar(vistas);
      const params = new URLSearchParams({
        amount: String(aviso.cobro.importe / 100),
        date: monthUtils.currentDay(),
        notes: 'Rumbo',
      });
      void navigate(`/transactions/new?${params.toString()}`, {
        state: { categoryId: id },
      });
    } finally {
      setOcupado(false);
    }
  };

  let titulo: string;
  let cuerpo: ReactNode;
  let botones: ReactNode;
  const ahoraNo = (
    <Boton
      variante="fantasma"
      style={{ flex: 1 }}
      data-testid="prueba-ahora-no"
      isDisabled={ocupado}
      onPress={() => void cerrar(vistas)}
    >
      <Trans>Not now</Trans>
    </Boton>
  );
  const botonAbrirPlan = (
    <Boton
      variante="fantasma"
      style={{ flex: 1 }}
      data-testid="prueba-abrir-plan"
      isDisabled={ocupado}
      onPress={abrirPlan}
    >
      <Trans>Open Plan</Trans>
    </Boton>
  );

  if (aviso.tipo === 'pago') {
    const sobra = sobraConDescuento(aviso.cobro, plan.precios?.mensual ?? null);
    const txt = textoPago(t, aviso.cobro, {
      importe: eur(aviso.cobro.importe),
      sobra: sobra != null ? eur(sobra) : null,
    });
    titulo = txt.cuerpo;
    cuerpo = txt.descuento && (
      <Text style={{ ...texto.secundario, color: color.fg2 }}>
        <PrivacyFilter>{txt.descuento}</PrivacyFilter>
      </Text>
    );
    botones = (
      <>
        <Boton
          bloque
          data-testid="prueba-apuntar-cobro"
          isDisabled={ocupado}
          onPress={() => void apuntarCobro()}
        >
          <Trans>Yes, note it</Trans>
        </Boton>
        <View style={{ flexDirection: 'row', gap: 10 }}>{ahoraNo}</View>
      </>
    );
  } else if (aviso.tipo === 'ingreso') {
    titulo = textoIngreso(t, fecha);
    botones = (
      <>
        <Boton
          bloque
          data-testid="prueba-apartar"
          isDisabled={ocupado}
          onPress={() => void apartar(aviso.falta)}
        >
          <PrivacyFilter>
            {t('Set aside {{amount}}', { amount: eur(aviso.falta) })}
          </PrivacyFilter>
        </Boton>
        <View style={{ flexDirection: 'row', gap: 10 }}>{ahoraNo}</View>
      </>
    );
  } else {
    const txt = textoHito(
      t,
      aviso.hito,
      aviso.quedan,
      situacion,
      {
        fecha,
        precio: eur(precio),
        ...(situacion.tipo === 'falta'
          ? { apartado: eur(situacion.apartado), falta: eur(situacion.falta) }
          : {}),
      },
      planificado,
    );
    titulo = txt.broma ? `${txt.cuerpo} ${txt.broma}` : txt.cuerpo;
    if (situacion.tipo === 'sin') {
      botones = (
        <>
          <Boton
            bloque
            data-testid="prueba-crear"
            isDisabled={ocupado}
            onPress={() => void crear()}
          >
            <Trans>Yes, create it</Trans>
          </Boton>
          <View style={{ flexDirection: 'row', gap: 10 }}>
            <Boton
              variante="fantasma"
              style={{ flex: 1 }}
              data-testid="prueba-no-gracias"
              isDisabled={ocupado}
              onPress={() => void cerrar(vistas)}
            >
              <Trans>No, thanks</Trans>
            </Boton>
          </View>
        </>
      );
    } else {
      const principal =
        aviso.hito === 'fin' ? (
          <Boton
            bloque
            data-testid="prueba-seguir"
            isDisabled={ocupado}
            onPress={seguirConRumbo}
          >
            <Trans>Keep going with Rumbo</Trans>
          </Boton>
        ) : situacion.tipo === 'falta' ? (
          <Boton
            bloque
            data-testid="prueba-elegir"
            isDisabled={ocupado}
            onPress={elegirDeDonde}
          >
            <Trans>Choose where from</Trans>
          </Boton>
        ) : null;
      botones = (
        <>
          {principal}
          {aviso.hito === 'fin' && situacion.tipo === 'falta' && (
            <Boton
              variante="tonal"
              bloque
              data-testid="prueba-elegir"
              isDisabled={ocupado}
              onPress={elegirDeDonde}
            >
              <Trans>Choose where from</Trans>
            </Boton>
          )}
          <View style={{ flexDirection: 'row', gap: 10 }}>
            {botonAbrirPlan}
            {ahoraNo}
          </View>
        </>
      );
    }
  }

  return (
    <Hoja
      aria-label={t('Gavi and your trial')}
      data-testid="hoja-prueba-rumbo"
      onCerrar={() => void cerrar(vistas)}
    >
      <View style={{ gap: 14, paddingTop: 6 }}>
        <Bocadillo
          variante="hoja"
          estado={aviso.tipo === 'pago' ? 'aplaude' : 'ladea'}
          texto={titulo}
        >
          {cuerpo}
        </Bocadillo>
        {botones}
      </View>
    </Hoja>
  );
}
